/**
 * Pure utility functions for style calculations
 * No side effects, fully testable
 */

export type MaxWidthType = "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl" | "7xl" | "90vw";

/**
 * Get the complete mapping of maxWidth values to Tailwind classes
 */
export function getMaxWidthClasses(): Record<MaxWidthType, string> {
  return {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl",
    "3xl": "max-w-3xl",
    "4xl": "max-w-4xl",
    "5xl": "max-w-5xl",
    "6xl": "max-w-6xl",
    "7xl": "max-w-7xl",
    "90vw": "w-[90vw] max-w-[90vw] min-h-[800px] max-h-[90vh]",
  };
}

/**
 * Get the Tailwind class for a specific maxWidth value
 */
export function getMaxWidthClass(maxWidth: MaxWidthType = "2xl"): string {
  const classes = getMaxWidthClasses();
  return classes[maxWidth] ?? classes["2xl"];
}

/**
 * Map maxWidth to Mantine Modal size
 * Mantine has limited sizes, so larger values map to "xl"
 */
export function getMantineSize(maxWidth: MaxWidthType = "2xl"): "sm" | "md" | "lg" | "xl" {
  switch (maxWidth) {
    case "sm":
      return "sm";
    case "md":
      return "md";
    case "lg":
      return "lg";
    case "xl":
    case "2xl":
    case "3xl":
    case "4xl":
    case "5xl":
    case "6xl":
    case "7xl":
    case "90vw":
      return "xl";
    default:
      return "lg";
  }
}

/**
 * Get CSS variable overrides for 90vw modal sizing
 */
export function get90vwStyleOverride(): React.CSSProperties | undefined {
  return {
    "--modal-size": "90vw",
    "--modal-y-offset": "5vh",
    "--modal-x-offset": "5vw",
  } as React.CSSProperties;
}

/**
 * Check if maxWidth requires special 90vw handling
 */
export function is90vwWidth(maxWidth: MaxWidthType = "2xl"): boolean {
  return maxWidth === "90vw";
}
